/**
 * Session-level attendance lock rules. Pure data-in/data-out — no React, no
 * i18n — so TodaySessionCard and session detail read the same rule instead of
 * re-deriving it from each participant's `attendanceLocked` flag (itself
 * computed API-side to mirror the I5 chốt-sổ guard exactly — see
 * apps/api/src/routes/sessions.ts's `lockedPeriodsForRows`). See
 * docs/superpowers/specs/2026-08-22-sessions-fold-design.md §3.
 */

interface LockParticipant {
  attendanceLocked: boolean;
  lockedPeriodLabel: string | null;
}

/**
 * A session counts as locked only when it has participants AND every one of
 * them is locked — a mixed roster (e.g. one PER_SESSION+MONTHLY student
 * reconciled, one TERM student not) must still allow editing the unlocked
 * rows, so the card-level "Đã chốt sổ" badge/footer-hiding only fires once
 * nothing on the card can be changed. An empty participant list is never
 * "locked" — there is nothing to have chốt sổ.
 */
export function isSessionLocked(participants: LockParticipant[]): boolean {
  return participants.length > 0 && participants.every((p) => p.attendanceLocked);
}

/**
 * The month label to show in the "Đã chốt sổ tháng {{month}}" badge/note —
 * the first non-null `lockedPeriodLabel` among the participants. All locked
 * participants on one session share the same session month, so any one of
 * them (the first found) is representative.
 */
export function lockedMonthOf(participants: LockParticipant[]): string | null {
  return participants.find((p) => p.lockedPeriodLabel != null)?.lockedPeriodLabel ?? null;
}

/**
 * The API's `lockedPeriodLabel` is a sortable 'YYYY-MM'; the lock copy reads
 * "tháng {{month}}", where a Vietnamese teacher says "tháng 7/2026" — no
 * leading zero, and the word "tháng" already supplied by the sentence (so
 * `formatMonthVi`, which prepends its own, is not what this needs). Returns
 * '' for a null/malformed label so a caller can never interpolate "undefined".
 */
export function fmtLockedMonth(label: string | null): string {
  if (!label || !/^\d{4}-\d{2}$/.test(label)) return '';
  const [y, m] = label.split('-') as [string, string];
  return `${Number(m)}/${y}`;
}
